import React from 'react';
import BaseForgeCustomizer from '../../base-forge';
import { userPasswordResetInit, submitLegacyPasswordResetForm, isRetryableError, getTransformedError } from '../../../utils/password-reset-api';
import AmplitudeUtils from '../../../utils/amplitude-utils';
import { getOktaWidgetModel, startSpinner, stopSpinner } from '../../../utils/widget-facade';
import { ERROR_KEYS, ERROR_MESSAGES } from '../../../constants';
import InfoContainer from '../../../components/common/InfoContainer';
import ForgotUsernameLink from '../../../components/password-reset/ForgotUsernameLink';
import ForgotUsernamePage from '../../../components/password-reset/ForgotUsernamePage';
import { createWrapper } from '../../../utils/react-utils';
import BackToLogin from '../../../components/common/BackToLogin';
import CommonFooter from '../../../components/common/Footer';
import { indefiniteWaitPromise } from '../../../utils/common-utils';

/**
 * Customizer for forgot password flow
 * Intercepts idx.proceed to check eligibility before OSW processes the request
 * Also handles forgot username flow
 */
export default class PasswordResetInitCustomizer extends BaseForgeCustomizer {
  async shouldCustomize() {
    return this.context?.controller === 'forgot-password' &&
    this.context?.formName === 'identify-recovery';
  }

  async customize() {
    // Check sessionStorage to see if we should show forgot username page
    const showForgotUsername = sessionStorage.getItem('showForgotUsernamePage') === 'true';
    const showSuccessBanner = sessionStorage.getItem('showForgotUsernameSuccessBanner') === 'true';

    if (showForgotUsername) {
      // Show forgot username page instead of password reset form
      await this.renderForgotUsernamePageOnly();
      // Clear the flag after rendering so it doesn't persist if user navigates away
      sessionStorage.removeItem('showForgotUsernamePage');
    }
    else {
      // Normal password reset flow
      await super.customize();
      this.interceptPasswordReset();

      // Show success banner if returning from forgot username flow
      if (showSuccessBanner) {
        this.showSuccessBanner();
        // Clear the flag from sessionStorage after showing banner
        sessionStorage.removeItem('showForgotUsernameSuccessBanner');
      }
    }
  }

  /**
   * Intercept OSW's idx.proceed to check eligibility before proceeding
   */
  protected interceptPasswordReset() {
    const {
      oktaSignIn: { authClient },
    } = this;

    const originalProceed = authClient.idx.proceed.bind(authClient.idx);

    // Widget calls this method after performing frontend validation
    // See https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L284
    authClient.idx.proceed = async (...args: any) => {
      // Check if this is the identify step (username entry)
      if (args[0]?.identifier) {
        const username = args[0].identifier.split('@')[0];
        const model = getOktaWidgetModel();

        // This captures the "normalized" error payload from the widget
        // See https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L385
        model.once('error', (_: any, error: any) => {
          const errorMessage =
            error?.responseJSON?.errorCauses?.[0]?.errorSummary ??
            error?.responseJSON?.errorSummary ??
            error?.message ??
            ERROR_MESSAGES[ERROR_KEYS.GENERIC_INTERNAL_ERROR];

          AmplitudeUtils.logCustomEvent('PasswordReset::InitFailed', {
            errorMessage,
            errorCode: error?.errorCode,
            username,
          });
        });

        // Log analytics
        AmplitudeUtils.logCustomEvent('PasswordReset::UsernameSubmitted', {
          username,
        });

        // Call Lambda for eligibility check AND group assignment
        // IMPORTANT: This API call does THREE things:
        // 1. Checks if user is eligible for new password reset flow
        // 2. Checks EPCS status by calling Monolith internally
        // 3. Assigns user to appropriate Okta group:
        //    - Regular users → "ResetPassword athenaOne Users" (MFA required)
        //    - EPCS users → "ResetPassword EPCS athenaOne Users" (No MFA)

        // ALPHA TESTING SAFETY: If this fails, redirect to legacy workflow
        let response;
        try {
          startSpinner({ forceDisplaySpinner: true });
          response = await userPasswordResetInit({
            username,
            clientId: this.getClientId(),
          });

          // Log result
          AmplitudeUtils.logCustomEvent('PasswordReset::InitCompleted', {
            username,
            eligible: response.eligible,
            useLegacyWorkflow: response.useLegacyWorkflow,
          });
        }
        catch (error) {
          stopSpinner();
          if (isRetryableError(error)) {
            // ALPHA TESTING SAFETY: Fallback to legacy workflow on any error
            AmplitudeUtils.logCustomEvent('PasswordReset::InitFailedFallbackToLegacy', {
              username,
              error: (error as any)?.message || 'Unknown error',
            });
            return submitLegacyPasswordResetForm(username);
          }
          // Throw error to show message on the error banner
          throw getTransformedError(error);
        }

        if (!response.eligible) {
          stopSpinner();
          // User is not eligible - show warning message and hide form
          const warningMessage = response.message || ERROR_MESSAGES[ERROR_KEYS.GENERIC_INTERNAL_ERROR];

          // Hide the input field container and button bar
          setTimeout(() => {
            const fieldsetContainer = document.querySelector('[data-se="o-form-fieldset-container"]');
            const buttonBar = document.getElementById('athena-o-form-button-bar');

            if (fieldsetContainer) {
              fieldsetContainer.classList.add('hide-element');
            }

            if (buttonBar) {
              buttonBar.classList.add('hide-element');
            }
          }, 0);

          // Show warning banner using event emitter
          this.eventEmitter.emitEventToComponent({
            componentName: InfoContainer.displayName as string,
            eventName: 'set-info-banner',
            eventPayload: {
              headerText: '',
              description: warningMessage,
              alertType: 'attention',
            },
          });

          // Return a Promise that never resolves to prevent form submission
          // This stops the widget from processing further without triggering an error
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          return indefiniteWaitPromise();
        }

        if (response.eligible && response.useLegacyWorkflow) {
          stopSpinner();
          // User is eligible but should use legacy workflow
          return submitLegacyPasswordResetForm(username);
        }

        // User is eligible and should use new workflow - continue OSW flow
        const result = originalProceed.apply(authClient.idx, args);
        stopSpinner();
        return result;
      }

      // For other steps, just proceed normally
      return originalProceed.apply(authClient.idx, args);
    };
  }

  private getClientId(): string {
    return this.settings.clientId || '';
  }

  /**
   * Override getFooter to add "Forgot username?" link above "Back to login"
   */
  protected getFooter({ footerLink }: { footerLink: JQuery }) {
    const footers = [];

    // Only show "Forgot username?" link on password reset page, not on forgot username page
    const showForgotUsername = sessionStorage.getItem('showForgotUsernamePage') !== 'true';
    if (showForgotUsername) {
      footers.push(
        <div key="forgot-username">
          <ForgotUsernameLink onClick={() => this.toggleForgotUsernamePage()} />
        </div>
      );
    }

    // Add "Back to login" link (always show)
    if (footerLink.length) {
      footers.push(
        <div key="back-to-login">
          <BackToLogin oktaButton={footerLink} />
        </div>
      );
    }

    return <CommonFooter reactFooters={footers} />;
  }

  /**
   * Render only the forgot username page (hide all other elements)
   */
  protected async renderForgotUsernamePageOnly() {
    // Initialize renderReactComponent first
    await super.customize();

    // Hide all the Forge-rendered elements except footer
    setTimeout(() => {
      // Hide Forge header
      const forgeHeader = document.getElementById('athena-o-form-head');
      if (forgeHeader) {
        forgeHeader.classList.add('hide-element');
      }

      // Hide Forge input fields
      const forgeInputs = document.querySelectorAll('[id^="athena-"][id$="-input-field-container"]');
      forgeInputs.forEach((input) => {
        input.classList.add('hide-element');
      });

      // Hide Forge button bar
      const forgeButtonBar = document.getElementById('athena-o-form-button-bar');
      if (forgeButtonBar) {
        forgeButtonBar.classList.add('hide-element');
      }

      // Keep the footer visible - it will show "Back to login" button
      // The getFooter() method will hide the "Forgot username?" link automatically

      // Create wrapper for forgot username page
      const wrapper = createWrapper({ hidden: false });
      const formContent = document.querySelector('[data-se="o-form-content"]');
      if (formContent) {
        formContent.appendChild(wrapper);
      }

      // Render forgot username page
      this.renderReactComponent({
        id: 'forgot-username-page',
        component: (
          <ForgotUsernamePage
            onSuccess={(email) => this.handleForgotUsernameSuccess(email)}
          />
        ),
        container: wrapper,
      });
    }, 0);
  }

  /**
   * Toggle between password reset page and forgot username page
   */
  protected toggleForgotUsernamePage() {
    // Check current state from sessionStorage
    const currentState = sessionStorage.getItem('showForgotUsernamePage') === 'true';
    const newState = !currentState;

    AmplitudeUtils.logCustomEvent('ForgotUsername::PageToggled', {
      showForgotUsernamePage: newState,
    });

    if (newState) {
      // Store flag to show forgot username page
      sessionStorage.setItem('showForgotUsernamePage', 'true');
    }
    else {
      // Clear flag to show password reset page
      sessionStorage.removeItem('showForgotUsernamePage');
    }

    // Trigger re-render by reloading the page
    // This is the simplest approach that works with OSW's architecture
    window.location.reload();
  }

  /**
   * Handle successful forgot username submission
   */
  protected handleForgotUsernameSuccess(email: string) {
    // Clear the forgot username page flag
    sessionStorage.removeItem('showForgotUsernamePage');

    // Store flag in sessionStorage to show success banner after page reload
    sessionStorage.setItem('showForgotUsernameSuccessBanner', 'true');

    // Reload to show password reset page with success banner
    window.location.reload();
  }

  /**
   * Show success banner after forgot username email is sent
   */
  protected showSuccessBanner() {
    // Show success banner
    setTimeout(() => {
      this.eventEmitter.emitEventToComponent({
        componentName: InfoContainer.displayName as string,
        eventName: 'set-info-banner',
        eventPayload: {
          headerText: 'Email Sent',
          description: 'Please check your email inbox for a message with your username enclosed. Enter your username below to reset your password.',
          alertType: 'success',
        },
      });
    }, 100);
  }
}
